import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, KeyRound, Mail, ShieldCheck, UserRound } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import { fetchCurrentUser, fetchMyBookings, getCurrentSession } from '../lib/portalApi';
import { changeMyPassword, updateMyProfile } from '../lib/adminApi';

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

const euro = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });

export default function AdminAccount() {
  const queryClient = useQueryClient();
  const session = getCurrentSession();
  const [pw, setPw] = useState({ current: '', next: '' });
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [profileMsg, setProfileMsg] = useState({ ok: '', err: '' });

  const userQuery = useQuery({ queryKey: ['auth', 'me'], queryFn: fetchCurrentUser, retry: false });
  const bookingsQuery = useQuery({ queryKey: ['me', 'bookings'], queryFn: fetchMyBookings, retry: false });
  const user = userQuery.data || session.user;
  const bookings = bookingsQuery.data || [];

  const [profile, setProfile] = useState({ full_name: '', phone: '' });
  // Seed the editable fields once the profile arrives from the API.
  useEffect(() => {
    if (!user) return;
    setProfile({ full_name: user.full_name || user.name || '', phone: user.phone || '' });
  }, [user?.full_name, user?.name, user?.phone]);

  const profileMutation = useMutation({
    mutationFn: () => updateMyProfile(profile),
    onSuccess: async () => {
      setProfileMsg({ ok: 'Datos actualizados.', err: '' });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (e) => setProfileMsg({ ok: '', err: e.message || 'No se pudieron guardar los datos.' }),
  });

  const passwordMutation = useMutation({
    mutationFn: () => changeMyPassword(pw.current, pw.next),
    onSuccess: () => {
      setMsg({ ok: 'Contraseña actualizada correctamente.', err: '' });
      setPw({ current: '', next: '' });
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo cambiar la contraseña.' }),
  });

  return (
    <AdminSection
      eyebrow="Admin"
      title="Mi cuenta"
      description="Tus datos de acceso al panel y tus reservas."
      widthClassName="max-w-5xl"
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#14213d]">
              <UserRound className="h-6 w-6 text-[#c8a25a]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#14213d]">Mis datos</h2>
              <p className="text-sm text-slate-500">Edita tu nombre y tu teléfono.</p>
            </div>
          </div>

          {profileMsg.ok ? <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{profileMsg.ok}</div> : null}
          {profileMsg.err ? <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{profileMsg.err}</div> : null}

          <div className="mt-5 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Nombre</label>
              <input className={`${inputCls} mt-1.5`} value={profile.full_name} placeholder="Tu nombre"
                onChange={(e) => setProfile((c) => ({ ...c, full_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Teléfono</label>
              <input className={`${inputCls} mt-1.5`} value={profile.phone} placeholder="+34 600 000 000"
                onChange={(e) => setProfile((c) => ({ ...c, phone: e.target.value }))} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <Mail className="h-4 w-4 flex-shrink-0 text-[#1fa5a3]" />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Email</p>
                  <p className="truncate text-sm font-medium text-[#14213d]">{user?.email || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <ShieldCheck className="h-4 w-4 flex-shrink-0 text-[#1fa5a3]" />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Rol</p>
                  <p className="truncate text-sm font-medium text-[#14213d]">{user?.role || 'customer'}</p>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Para cambiar tu email o tu rol, edítalo desde <strong>Clientes</strong>.
            </p>

            <button
              type="button"
              disabled={profileMutation.isPending}
              onClick={() => { setProfileMsg({ ok: '', err: '' }); profileMutation.mutate(); }}
              className="w-full rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-50"
            >
              {profileMutation.isPending ? 'Guardando…' : 'Guardar datos'}
            </button>
          </div>
        </section>

        <section className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#14213d]">
              <KeyRound className="h-5 w-5 text-[#c8a25a]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#14213d]">Cambiar contraseña</h2>
              <p className="text-sm text-slate-500">Necesitas tu contraseña actual.</p>
            </div>
          </div>

          {msg.ok ? <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
          {msg.err ? <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

          <div className="mt-5 space-y-4">
            <input type="password" autoComplete="current-password" className={inputCls} placeholder="Contraseña actual"
              value={pw.current} onChange={(e) => setPw((c) => ({ ...c, current: e.target.value }))} />
            <input type="password" autoComplete="new-password" className={inputCls} placeholder="Nueva contraseña (8+, letras y números)"
              value={pw.next} onChange={(e) => setPw((c) => ({ ...c, next: e.target.value }))} />
            <button
              type="button"
              disabled={!pw.current || pw.next.length < 8 || passwordMutation.isPending}
              onClick={() => { setMsg({ ok: '', err: '' }); passwordMutation.mutate(); }}
              className="w-full rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-50"
            >
              {passwordMutation.isPending ? 'Guardando…' : 'Actualizar contraseña'}
            </button>
          </div>
        </section>
      </div>

      <section className="mt-6 min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-5 w-5 text-[#1fa5a3]" />
          <h2 className="text-xl font-bold text-[#14213d]">Mis reservas</h2>
          <span className="ml-auto rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{bookings.length}</span>
        </div>
        {bookings.length ? (
          <div className="mt-5 divide-y divide-slate-100">
            {bookings.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#14213d]">{b.activity_title || b.activity_id}</p>
                  <p className="text-xs text-slate-500">{b.service_date} · {b.time_slot}</p>
                </div>
                <span className="text-sm font-semibold text-[#14213d]">{euro.format(b.amount || 0)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm text-slate-500">Todavía no tienes reservas asociadas a esta cuenta.</p>
        )}
      </section>
    </AdminSection>
  );
}
